import { socketService } from "./socket";

/**
 * Structured client-side logger that mirrors every record to the browser
 * console AND ships a batched copy to the server, where it is re-emitted
 * through Pino. Lets us grep production-shaped diagnostics in /tmp/srv.log
 * instead of asking users to copy-paste DevTools output.
 *
 * Usage:
 *   import { clog } from "@/services/clientLogger";
 *   clog.info("handleInit received", { stories: data.stories.length });
 *   clog.warn("auto-creating story — no active found", { ... });
 *
 * Browser console still gets the call, so day-to-day debugging is unchanged.
 * Server logs each entry as: `[client] <msg>` with structured fields.
 */

type Level = "trace" | "debug" | "info" | "warn" | "error";

interface LogContext {
  [key: string]: unknown;
}

interface BufferedEntry {
  level: Level;
  msg: string;
  time: number;
  url: string;
  ctx: LogContext;
}

const FLUSH_INTERVAL_MS = 2_000;
const MAX_BUFFER = 100;
const SOCKET_EVENT = "client-log";

class ClientLogger {
  private buffer: BufferedEntry[] = [];
  private flushTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    if (typeof window === "undefined") return;
    this.flushTimer = setInterval(() => this.flush(), FLUSH_INTERVAL_MS);
    window.addEventListener("beforeunload", () => this.flush(true));
    // Drain whatever was buffered while disconnected once the socket comes back.
    socketService.onConnectionChange((connected) => {
      if (connected) this.flush();
    });
  }

  trace(msg: string, ctx?: LogContext) {
    this.write("trace", msg, ctx);
  }
  debug(msg: string, ctx?: LogContext) {
    this.write("debug", msg, ctx);
  }
  info(msg: string, ctx?: LogContext) {
    this.write("info", msg, ctx);
  }
  warn(msg: string, ctx?: LogContext) {
    this.write("warn", msg, ctx);
  }
  error(msg: string, ctx?: LogContext) {
    this.write("error", msg, ctx);
  }

  private write(level: Level, msg: string, ctx: LogContext = {}) {
    const entry: BufferedEntry = {
      level,
      msg,
      time: Date.now(),
      url: typeof window !== "undefined" ? window.location.pathname : "",
      ctx,
    };

    // Mirror to console — keep the inline DevTools UX.
    const consoleFn =
      level === "trace"
        ? console.debug
        : (console[level] as ((...args: unknown[]) => void) | undefined) || console.info;
    if (Object.keys(ctx).length > 0) {
      consoleFn(`[clog/${level}] ${msg}`, ctx);
    } else {
      consoleFn(`[clog/${level}] ${msg}`);
    }

    this.buffer.push(entry);
    if (this.buffer.length >= MAX_BUFFER) this.flush();
  }

  /** Send the current buffer to the server. No-op when offline. */
  flush(sync = false): void {
    if (this.buffer.length === 0) return;
    if (!socketService.connected) return; // wait for reconnect to drain
    const batch = this.buffer.splice(0);
    try {
      socketService.emit(SOCKET_EVENT, batch);
    } catch {
      // Best-effort. If the emit throws (e.g. mid-disconnect), restore
      // the batch so the next flush picks it up.
      if (!sync) this.buffer.unshift(...batch);
    }
  }
}

export const clog = new ClientLogger();
